import notesMenu from './notesMenu'

export interface AppModuleDefinition {
  name: string
  code: string
  path: string
  routePrefixes: string[]
  apiPrefixes: string[]
}

const noteAppModules: AppModuleDefinition[] = notesMenu.map((item) => ({
  name: item.name,
  code: item.code,
  path: item.path,
  routePrefixes: [item.path],
  apiPrefixes: item.apiPrefixes || [],
}))

const topLevelAppModules: AppModuleDefinition[] = [
  {
    name: 'Objectives',
    code: 'objectives',
    path: '/objectives',
    routePrefixes: ['/objectives', '/objective/'],
    apiPrefixes: ['/objectives', '/steps'],
  },
  {
    name: 'Problems',
    code: 'problems',
    path: '/problems',
    routePrefixes: ['/problems'],
    apiPrefixes: ['/problems'],
  },
  {
    name: 'Events',
    code: 'events',
    path: '/events',
    routePrefixes: ['/events'],
    apiPrefixes: ['/events'],
  },
  {
    name: 'Translation',
    code: 'translation',
    path: '/translation',
    routePrefixes: ['/translation'],
    apiPrefixes: ['/vocabularies', '/words'],
  },
  {
    name: 'Whiteboards',
    code: 'whiteboards',
    path: '/whiteboards',
    routePrefixes: ['/whiteboards'],
    apiPrefixes: ['/whiteboards'],
  },
  {
    name: 'Files',
    code: 'files',
    path: '/files',
    routePrefixes: ['/files'],
    apiPrefixes: ['/files/folders'],
  },
  {
    name: 'Lunar',
    code: 'recordings',
    path: '/recordings',
    routePrefixes: ['/recordings'],
    apiPrefixes: ['/lunar/library'],
  },
  {
    name: 'Scenarios',
    code: 'scenarios',
    path: '/scenarios',
    routePrefixes: ['/scenarios'],
    apiPrefixes: ['/scenarios'],
  },
  {
    name: 'Purchases',
    code: 'purchases',
    path: '/purchases?tab=daily',
    routePrefixes: [],
    apiPrefixes: [
      '/purchases/daily',
      '/purchases/big',
      '/purchases/comparison',
    ],
  },
  {
    name: 'Utility bills',
    code: 'utility-bills',
    path: '/purchases?tab=utilities&utility=electricity',
    routePrefixes: [],
    apiPrefixes: ['/purchases/utilities'],
  },
  {
    name: 'Spend analytics',
    code: 'spend-analytics',
    path: '/purchases?tab=analytics',
    routePrefixes: [],
    apiPrefixes: [],
  },
  {
    name: 'Income',
    code: 'income',
    path: '/income',
    routePrefixes: ['/income'],
    apiPrefixes: ['/income'],
  },
  {
    name: 'Income analytics',
    code: 'income-analytics',
    path: '/income?tab=analytics',
    routePrefixes: [],
    apiPrefixes: [],
  },
  {
    name: 'Settings',
    code: 'settings',
    path: '/settings/users',
    routePrefixes: ['/settings'],
    apiPrefixes: ['/users'],
  },
]

export const appModulesForLanding: AppModuleDefinition[] = [
  ...noteAppModules,
  ...topLevelAppModules,
]

export const appModules: AppModuleDefinition[] = [...appModulesForLanding].sort(
  (left, right) => left.name.localeCompare(right.name),
)

const matchesPrefix = (value: string, prefix: string): boolean => {
  return value === prefix || value.startsWith(`${prefix}/`) || value.startsWith(prefix)
}

const matchLongestPrefix = (
  value: string,
  entries: Array<{ code: string; prefix: string }>,
): string | undefined => {
  return entries
    .filter(({ prefix }) => prefix && matchesPrefix(value, prefix))
    .sort((left, right) => right.prefix.length - left.prefix.length)[0]?.code
}

export const getPurchaseSectionModule = (
  search: string = '',
): 'purchases' | 'utility-bills' | 'spend-analytics' => {
  const tab = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  ).get('tab')

  if (tab === 'utilities') {
    return 'utility-bills'
  }

  if (tab === 'analytics') {
    return 'spend-analytics'
  }

  return 'purchases'
}

export const getIncomeSectionModule = (
  search: string = '',
): 'income' | 'income-analytics' => {
  const tab = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  ).get('tab')

  if (tab === 'analytics') {
    return 'income-analytics'
  }

  return 'income'
}

export const matchModuleByRoutePath = (
  pathWithSearch: string,
): string | undefined => {
  const [rawPath, rawSearch = ''] = pathWithSearch.split('?')
  const path = rawPath || '/'

  if (path === '/purchases' || path.startsWith('/purchases/')) {
    return getPurchaseSectionModule(rawSearch)
  }

  if (path === '/income' || path.startsWith('/income/')) {
    return getIncomeSectionModule(rawSearch)
  }

  return matchLongestPrefix(
    path,
    appModules.flatMap((moduleItem) =>
      moduleItem.routePrefixes.map((prefix) => ({
        code: moduleItem.code,
        prefix,
      })),
    ),
  )
}

export const matchModuleByApiUrl = (url: string): string | undefined => {
  const path = (url.split('?')[0] || '').replace(/^\/api/, '') || '/'

  return matchLongestPrefix(
    path.startsWith('/') ? path : `/${path}`,
    appModules.flatMap((moduleItem) =>
      moduleItem.apiPrefixes.map((prefix) => ({
        code: moduleItem.code,
        prefix,
      })),
    ),
  )
}
